import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot, updateDoc, doc, writeBatch, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Notification, ReactionType } from '../../types';
import { Bell, Heart, Flame, Trophy, MessageCircle } from 'lucide-react';
import { formatRelativeTime } from '../../utils/formatters';
import { prefetchRoute } from '../../router';

interface Props {
  userId: string;
  isMobile?: boolean;
}

const REACTION_ICONS = {
  [ReactionType.LOVE]: <Heart size={12} fill="currentColor" />,
  [ReactionType.FIRE]: <Flame size={12} fill="currentColor" />,
  [ReactionType.KUDOS]: <Trophy size={12} fill="currentColor" />,
};

const getReactionColor = (type?: ReactionType) => {
  switch (type) {
    case ReactionType.LOVE: return 'text-red-500';
    case ReactionType.FIRE: return 'text-orange-500';
    case ReactionType.KUDOS: return 'text-yellow-500';
    default: return 'text-indigo-500';
  }
};

const NotificationsPopover: React.FC<Props> = ({ userId, isMobile = false }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userId) return;

    const q = query(
      collection(db, 'users', userId, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetched = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Notification));
      setNotifications(fetched);
      setUnreadCount(fetched.filter(n => !n.read).length);
    });

    return () => unsubscribe();
  }, [userId]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // If mobile portal, we handle close via backdrop or explicit close, but also good to have document click listener
      // However, with portal, event.target might not be in containerRef.
      // For now, let's keep it simple: clicking outside closes it.
      // If isMobile, we can rely on a backdrop div (see below) or just global click.

      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // logic for non-portal or if click is outside bell button
        if (!isMobile) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  const markAllAsRead = async () => {
    const batch = writeBatch(db);
    const unread = notifications.filter(n => !n.read);
    if (unread.length === 0) return;

    unread.forEach(n => {
      const ref = doc(db, 'users', userId, 'notifications', n.id);
      batch.update(ref, { read: true });
    });

    try {
      await batch.commit();
    } catch (error) {
      console.error("Error marking read:", error);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  const navigate = useNavigate();

  const handleNotificationClick = (n: Notification) => {
    // Fire-and-forget: mark as read without blocking navigation
    if (!n.read) {
      updateDoc(doc(db, 'users', userId, 'notifications', n.id), { read: true })
        .catch(err => console.error("Error marking read:", err));
    }

    // Navigate immediately
    setIsOpen(false);
    navigate(`/user/${n.activityUserId || userId}/activity/${n.activityId}`);
  };

  const dropdownContent = (
    <>
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-[49] bg-black/5"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div className={
        isMobile
          ? "fixed top-[70px] left-4 right-4 z-[50] bg-white/90 backdrop-blur-2xl rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 ring-1 ring-white/20"
          : "absolute right-0 top-full mt-2 w-[400px] bg-white/90 backdrop-blur-2xl rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/50 overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200 ring-1 ring-white/20"
      }>
        <div className="p-3 border-b border-indigo-100/30 flex justify-between items-center bg-white/50 backdrop-blur-sm gap-2">
          <h3 className="font-bold text-slate-700 text-sm whitespace-nowrap">Thông báo</h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-[10px] text-indigo-600 font-semibold hover:text-indigo-800 uppercase tracking-wide bg-indigo-50 px-2 py-1 rounded-full transition-colors whitespace-nowrap hover:bg-indigo-100"
            >
              Đánh dấu đã đọc
            </button>
          )}
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-xs">
              Không có thông báo mới
            </div>
          ) : (
            notifications.map(n => (
              <button
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                onMouseEnter={() => prefetchRoute(`/user/${n.activityUserId || userId}/activity/${n.activityId}`)}
                onTouchStart={() => prefetchRoute(`/user/${n.activityUserId || userId}/activity/${n.activityId}`)}
                className={`w-full text-left p-3 border-b border-indigo-50/30 hover:bg-indigo-50/40 transition-colors flex gap-3 ${!n.read ? 'bg-indigo-50/60' : 'bg-transparent'}`}
              >
                <div className="relative flex-shrink-0">
                  <img src={n.senderAvatar} alt={n.senderName} className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm bg-slate-200" />
                  <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm ring-1 ring-white">
                    {/* Icon based on type */}
                    {n.type === 'reaction' && n.reactionType ? (
                      <div className={getReactionColor(n.reactionType)}>
                        {REACTION_ICONS[n.reactionType]}
                      </div>
                    ) : n.type === 'reaction' ? (
                      <div className="text-red-500"><Heart size={12} fill="currentColor" /></div>
                    ) : n.type === 'mixed' ? (
                      <div className="text-purple-500"><Flame size={12} fill="currentColor" /></div>
                    ) : (
                      <div className="text-blue-500"><MessageCircle size={12} fill="currentColor" /></div>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-800 line-clamp-2 leading-relaxed">
                    <span className="font-bold text-slate-900">{n.senderName}</span> {n.message}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1 font-medium">
                    {formatRelativeTime(new Date(n.createdAt))}
                  </p>
                </div>
                {!n.read && (
                  <div className="flex-shrink-0 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 shadow-sm shadow-indigo-500/50"></div>
                  </div>
                )}
              </button>
            ))
          )}
        </div >
      </div >
    </>
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 text-slate-500 hover:text-indigo-600 hover:bg-white/50 rounded-full transition-all active:scale-95"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full animate-pulse border border-white shadow-sm">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (isMobile ? createPortal(dropdownContent, document.body) : dropdownContent)}
    </div>
  );
};

export default NotificationsPopover;
